import { ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import { MessageSeverity } from "@sinch/core/entities/serverEnums";
import { MdsStar } from "@sinch/icons";
import { parseMutationErrorsFromData } from "@sinch/utils/apollo";
import { useSnackbar } from "notistack";
import { propEq } from "ramda";
import { isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { useRatelist } from "@sinch/components/application/Rate/useRatelist";
import { ConfirmDialogWrapper } from "@sinch/components/ConfirmDialogWrapper/ConfirmDialogWrapper";

export const SetDefaultMenuItem = ({ id, onPerformedAction }: { id: string; onPerformedAction: () => void }) => {
  const { t } = useTranslation(["common", "draft"]);
  const { setDefault } = useRatelist();

  const { enqueueSnackbar } = useSnackbar();

  const handleConfirm = useCallback(async () => {
    try {
      const response = await setDefault({ variables: { ratelistId: id } });
      if (response.data?.setDefaultRatelist.result) {
        onPerformedAction();
        enqueueSnackbar(t("successfullySaved"), { variant: "success" });
      } else {
        const firstMessage = parseMutationErrorsFromData(response).find((message) =>
          propEq("severity", MessageSeverity.Error)(message)
        )?.message;
        if (isNotNilOrEmpty(firstMessage)) {
          enqueueSnackbar(firstMessage, { variant: "error" });
        }
      }
    } catch {
      enqueueSnackbar(t("ratelistError"), { variant: "error" });
    }
  }, [setDefault, id, onPerformedAction, enqueueSnackbar, t]);

  return (
    <ConfirmDialogWrapper
      content={<Typography>{t("RateBuilder.confirmSetDefault")}</Typography>}
      label={t("RateBuilder.confirmSetDefaultTitle")}
      onConfirm={handleConfirm}
      render={({ openConfirmation }) => (
        <MenuItem color="info" onClick={openConfirmation}>
          <ListItemIcon>
            <MdsIcon color="info" icon={MdsStar} />
          </ListItemIcon>
          <ListItemText sx={{ color: "info.main" }}>{t("RateBuilder.setAsDefault")}</ListItemText>
        </MenuItem>
      )}
    />
  );
};
